import { useState } from 'react'
import { X } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../config/api'
import { toast } from './Toast'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from './ui'
import { inputClass } from '../styles/forms'
import { formatPhone } from '../utils/masks'

interface RegisterActivityModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  voter: { id: number; name: string; phone: string } | null
}

const AUTOMATIC_CODES = ['CREATED', 'UPDATED', 'PROMOTED', 'CAMPAIGN', 'DEMAND_CREATED', 'DEMAND_COMPLETED', 'DEMAND_CANCELLED']

export default function RegisterActivityModal({ open, onClose, onSuccess, voter }: RegisterActivityModalProps) {
  const [activityTypes, setActivityTypes] = useState<{ id: number; code: string; label: string }[]>([])
  const [loaded, setLoaded] = useState(false)
  const [form, setForm] = useState({ action_type_id: '', description: '' })
  const [files, setFiles] = useState<File[]>([])
  const [saving, setSaving] = useState(false)

  // Carregar tipos ao abrir
  if (open && !loaded) {
    axios.get(buildApiUrl('/v1/voters/action-types')).then(r => {
      setActivityTypes((r.data.data || []).filter((t: any) => !AUTOMATIC_CODES.includes(t.code)))
      setLoaded(true)
    }).catch(() => setLoaded(true))
  }

  // Resetar ao abrir
  if (!open && (form.action_type_id || form.description || files.length > 0)) {
    setForm({ action_type_id: '', description: '' })
    setFiles([])
  }

  async function handleSave() {
    if (saving || !voter || !form.action_type_id) return
    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('action_type_id', form.action_type_id)
      if (form.description) formData.append('description', form.description)
      files.forEach(f => formData.append('files[]', f))
      await axios.post(buildApiUrl(`/v1/voters/${voter.id}/activity`), formData)
      toast('success', 'Atividade registrada')
      onClose()
      onSuccess?.()
    } catch { toast('error', 'Erro ao registrar') } finally { setSaving(false) }
  }

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalHeader onClose={onClose}>Registrar Atividade</ModalHeader>
      <ModalBody>
        {voter && (
          <div className="mb-4 p-3 bg-gray-50 rounded-xl">
            <p className="text-sm font-medium text-gray-900">{voter.name}</p>
            <p className="text-xs text-gray-500">{formatPhone(voter.phone)}</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de atividade</label>
            <select value={form.action_type_id} onChange={e => setForm(f => ({ ...f, action_type_id: e.target.value }))} className={clsx(inputClass, 'appearance-none')}>
              <option value="">Selecione</option>
              {[...activityTypes].sort((a, b) => a.label.localeCompare(b.label)).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Anexos</label>
            {files.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-700 truncate">{f.name}</span>
                    <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-error-500 ml-2">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {files.length < 5 && (
              <label className="flex items-center justify-center h-10 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-brand-400 transition-colors">
                <span className="text-xs text-gray-400">+ Adicionar arquivo</span>
                <input type="file" className="hidden" multiple onChange={e => {
                  const newFiles = Array.from(e.target.files || [])
                  setFiles(prev => [...prev, ...newFiles].slice(0, 5))
                  e.target.value = ''
                }} />
              </label>
            )}
            <p className="text-[11px] text-gray-400 mt-1">Máximo 5 arquivos, 10MB cada</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Observação</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2} placeholder="Detalhes da atividade..." className={clsx(inputClass, 'resize-none')} />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} loading={saving} disabled={!form.action_type_id}>Registrar</Button>
      </ModalFooter>
    </Modal>
  )
}
